import { forwardRef } from 'react'
import type { LeaderboardEntry, Spotlight, TeamStanding } from '@gymboard/types'
import { designTokens } from '@gymboard/types'

export const ZONES = designTokens.zones
const UNKNOWN_ZONE = designTokens.zoneUnknown

export function zoneIndex(zone: string | null | undefined): number {
  return ZONES.findIndex((z) => z.key === zone)
}

export function zoneHex(zone: string | null | undefined): string {
  return ZONES[zoneIndex(zone)]?.color ?? UNKNOWN_ZONE
}

function rgba(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
}

export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

/** Why the hero is on stage, in the room's language. */
export function spotlightReason(s: Spotlight, entry: LeaderboardEntry): string {
  switch (s.reason) {
    case 'lead': return 'Took the lead'
    case 'zone5': return 'Hit Zone 5'
    case 'climb': return `Moved up to ${ordinal(s.rank)}`
    case 'coach': return "Coach's pick"
    default: return entry.rank === 1 ? 'Leading the class' : `Holding ${ordinal(entry.rank)} place`
  }
}

export function ZoneChip({ zone, className = '' }: { zone: string | null | undefined; className?: string }) {
  const i = zoneIndex(zone)
  return (
    <span className={`sp-zc ${className}`} style={{ background: zoneHex(zone) }} aria-label={i >= 0 ? `Zone ${i + 1}` : 'No zone yet'}>
      {i >= 0 ? i + 1 : '–'}
    </span>
  )
}

export const ICON_SYNC = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="13" r="8" stroke="currentColor" strokeWidth="2.4" /><path d="M12 9v4l2.5 2M9 2h6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
)
export const ICON_PHONE = (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="6" y="2.5" width="12" height="19" rx="2.5" stroke="currentColor" strokeWidth="2.4" /><path d="M10.5 18h3" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
)

const MARK = (
  <svg viewBox="0 0 64 64" aria-hidden="true">
    <g transform="translate(4 4)">
      <path d="M14.9 45.2A24 24 0 1 1 41.1 45.2" fill="none" stroke="#F1EEE8" strokeWidth="5.5" strokeLinecap="round" />
      <path d="M28 12c5 7.5 11 12 11 20.5A11 11 0 0 1 17 32.5c0-5 2.6-8.4 5.3-11.3 1 3.3 2.8 5.3 5.2 6.3C28.4 21.8 27.5 16.8 28 12Z" fill="#C93A30" />
    </g>
  </svg>
)

interface Props {
  spotlight: Spotlight | null
  hero: LeaderboardEntry | null
  teams?: TeamStanding[]
  /** Bumped on every handoff so the supporting lines replay their entrance. */
  swapKey: string
  hasQr: boolean
  /** The hero's race in one line: the gap to the next place, or their lead. */
  story?: { lead: string; rest: string } | null
}

/**
 * The stage: one athlete at a time, at the scale the room reads from across
 * the floor. The zone flood rises with their share of max heart rate.
 */
const SpotlightStage = forwardRef<HTMLDivElement, Props>(function SpotlightStage(
  { spotlight, hero, teams, swapKey, hasQr, story },
  nameRef,
) {
  if (!spotlight || !hero) {
    return (
      <div className="sp-stage">
        <div className="sp-empty">
          {MARK}
          <p>Waiting for athletes</p>
          <small>{hasQr ? 'Scan the code to check in. Your name appears here once your heart rate comes through.' : 'Athletes appear once they check in and their heart rate comes through.'}</small>
        </div>
      </div>
    )
  }

  const zc = zoneHex(hero.hrZone)
  const zi = zoneIndex(hero.hrZone)
  const max = hero.maxHeartRate || 190
  const share = hero.heartRate ? hero.heartRate / max : 0
  const flood = Math.max(0.12, Math.min(1, (share - 0.5) / 0.5))
  const isPhone = hero.hrSource === 'phone_imu' && hero.effortScore != null
  const value = isPhone ? Math.round(hero.effortScore ?? 0) : hero.heartRate ? Math.round(hero.heartRate) : null
  const team = hero.team != null ? teams?.find((t) => t.team === hero.team) : undefined
  // Sadu band, two rows: the zone-coloured teeth lead, a camel row of
  // counter-teeth sits under them offset half a step, so the gaps read as
  // the weave's diamonds.
  const weave = {
    backgroundImage: [
      `linear-gradient(135deg, ${zc} 25%, transparent 25%)`,
      `linear-gradient(225deg, ${zc} 25%, transparent 25%)`,
      'linear-gradient(45deg, #D9B68A 25%, transparent 25%)',
      'linear-gradient(315deg, #D9B68A 25%, transparent 25%)',
    ].join(', '),
    backgroundSize: '1.5vw 1.5vw',
    backgroundPosition: '0 0, 0 0, 0.75vw 100%, 0.75vw 100%',
  }

  return (
    <div className="sp-stage">
      <div
        className="sp-flood"
        style={{ background: `linear-gradient(to top, ${rgba(zc, 0.42)}, ${rgba(zc, 0)} 92%)`, transform: `scaleY(${flood})` }}
      />
      <div key={swapKey} className="sp-inner sp-swap">
        <div className="sp-top">
          <div className="sp-why">{spotlightReason(spotlight, hero)}</div>
          <div className="sp-standing">
            {ordinal(hero.rank)}
            <small>{Math.round(hero.score)} {Math.round(hero.score) === 1 ? 'pt' : 'pts'}</small>
          </div>
        </div>
        <div className="sp-who" ref={nameRef}>{hero.athleteName}</div>
        {(team || isPhone || hero.hrStale) && (
          <div className="sp-tags">
            {team && <span className="sp-tag" style={{ color: team.color, background: rgba(team.color, 0.14) }}>Team {team.name}</span>}
            {isPhone && <span className="sp-tag phone">{ICON_PHONE}Phone motion</span>}
            {!isPhone && hero.hrStale && <span className="sp-tag sync">{ICON_SYNC}Heart rate delayed</span>}
          </div>
        )}
        {story && (
          <div className="sp-story">
            <b>{story.lead}</b> {story.rest}
          </div>
        )}
        <div className="sp-hr">
          <b style={{ color: value != null ? zc : '#66678A' }}>{value ?? '—'}</b>
          <span>{isPhone ? 'effort' : 'BPM'}</span>
        </div>
        <div className="sp-zone">
          <ZoneChip zone={hero.hrZone} />
          <span>{zi >= 0 ? ZONES[zi].name : 'Warming up'}</span>
        </div>
      </div>
      <div className="sp-weave" style={weave} />
    </div>
  )
})

export default SpotlightStage
